import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import "../assets/css/main.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {useNavigate, useSearchParams} from "react-router-dom";
import Header from "../ThemeComponents/Header";
import Footer from "../ThemeComponents/Footer";
import {
    getAccessoriesAPI,
    getAccessoriesCategoriesAPI,
    getAccessoriesLocationsAPI,
    getAccessoriesPartnersAPI
} from "../network/publicCalls/GETAccessoriesAttributes";
import {
    createAccessoriesDraftOrderAPI,
    createAccessoriesOrderPaymentAPI,
    createIdempotencyKey,
    preflightAccessoriesCheckoutAPI,
} from "../network/publicCalls/POSTAccessoriesCheckout";
import {isValidEmail} from "../config/toolKit";

const CART_STORAGE_KEY = "accessoriesCart";

const DEFAULT_CART_STATE = {
    purchaseQty: 0,
    rentQty: 0,
    rentDays: 1
};

const normalizePositiveInt = (value, min = 0) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return min;
    return Math.max(min, Math.floor(parsed));
};

const normalizeCartEntry = (entry = {}) => ({
    purchaseQty: normalizePositiveInt(entry.purchaseQty, 0),
    rentQty: normalizePositiveInt(entry.rentQty, 0),
    rentDays: normalizePositiveInt(entry.rentDays, 1),
});

const hasPositivePrice = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0;
};

const getTokenFromStorage = () => {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem("token") || null;
};

const parseCheckoutErrors = (response) => {
    const errorList = Array.isArray(response?.errors) ? response.errors : [];
    if (!errorList.length) return "";

    if (errorList.includes("cart_empty")) {
        return "Je winkelwagen is leeg.";
    }
    if (errorList.includes("contact_required")) {
        return "Vul je contactgegevens in om verder te gaan.";
    }
    if (errorList.includes("registration_required")) {
        return "Inloggen of registreren is verplicht voor huurproducten.";
    }
    return errorList.join(", ");
};

const toArray = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data)) return response.data;
    return [];
};

const uniqueById = (items = []) => {
    const map = new Map();
    items.forEach((item) => {
        const normalizedId = String(item.id);
        if (!map.has(normalizedId)) {
            map.set(normalizedId, {...item, id: normalizedId});
        }
    });
    return Array.from(map.values());
};

const getInitialCartState = () => {
    if (typeof window === "undefined" || !window.localStorage) return {};
    try {
        const raw = window.localStorage.getItem(CART_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return {};

        return Object.entries(parsed).reduce((carry, [accessoryId, entry]) => {
            carry[String(accessoryId)] = normalizeCartEntry(entry);
            return carry;
        }, {});
    } catch (error) {
        console.warn("Accessoires cart read error", error);
        return {};
    }
};

const AccessoiresPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedPartner, setSelectedPartner] = useState("all");
    const [selectedLocation, setSelectedLocation] = useState("all");
    const [locationSearch, setLocationSearch] = useState("");
    const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isCartOpen, setIsCartOpen] = useState(false);

    const [categories, setCategories] = useState([{id: "all", name: "Alle categorieën"}]);
    const [locations, setLocations] = useState([{id: "all", name: "Alle locaties"}]);
    const [partners, setPartners] = useState([{id: "all", name: "Alle partners"}]);
    const [accessories, setAccessories] = useState([]);
    const [accessoryIndex, setAccessoryIndex] = useState({});
    const [isLoadingAccessories, setIsLoadingAccessories] = useState(false);
    const [accessoriesError, setAccessoriesError] = useState("");
    const [cart, setCart] = useState(getInitialCartState);
    const [checkoutStep, setCheckoutStep] = useState("cart");
    const [checkoutError, setCheckoutError] = useState("");
    const [checkoutNotice, setCheckoutNotice] = useState("");
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [preflightResult, setPreflightResult] = useState(null);
    const [draftOrderResult, setDraftOrderResult] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null);
    const [checkoutContact, setCheckoutContact] = useState({
        name: "",
        email: "",
        phone: ""
    });

    const locationDropdownRef = useRef(null);
    const appliedSearchLocationRef = useRef("");
    const draftIdempotencyKeyRef = useRef("");
    const paymentIdempotencyKeyRef = useRef("");

    const eurFormatter = useMemo(() => new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }), []);

    const formatPriceEur = useCallback((value) => {
        const amount = Number(value);
        if (!Number.isFinite(amount)) return "--";
        return eurFormatter.format(amount);
    }, [eurFormatter]);

    const hasPurchasePrice = useCallback((item) => hasPositivePrice(item?.prices?.purchase_including_vat), []);
    const hasRentalPrice = useCallback((item) => hasPositivePrice(item?.prices?.rental_including_vat), []);

    const buildQuery = useCallback(() => ({
        categoryId: selectedCategory !== "all" ? selectedCategory : undefined,
        locationId: selectedLocation !== "all" ? selectedLocation : undefined,
        partnerId: selectedPartner !== "all" ? selectedPartner : undefined
    }), [selectedCategory, selectedLocation, selectedPartner]);

    const updateCart = useCallback((accessoryId, updater) => {
        const key = String(accessoryId);
        setCart((prev) => {
            const current = normalizeCartEntry(prev[key] || DEFAULT_CART_STATE);
            const next = updater(current);

            return {
                ...prev,
                [key]: normalizeCartEntry(next),
            };
        });
    }, []);

    const resolveAccessoryPartner = useCallback((item) => {
        const itemLocations = Array.isArray(item?.locations) ? item.locations : [];
        const selectedLocationMatch = selectedLocation !== "all"
            ? itemLocations.find((location) => String(location.id) === String(selectedLocation))
            : null;

        const targetLocation = selectedLocationMatch || itemLocations[0] || null;
        return targetLocation?.partner?.name || item?.subsidiary?.name || "-";
    }, [selectedLocation]);

    const fetchFilters = useCallback(async (query) => {
        const [categoriesResponse, locationsResponse, partnersResponse] = await Promise.all([
            getAccessoriesCategoriesAPI({
                locationId: query.locationId,
                partnerId: query.partnerId
            }),
            getAccessoriesLocationsAPI({
                categoryId: query.categoryId,
                partnerId: query.partnerId
            }),
            getAccessoriesPartnersAPI({
                categoryId: query.categoryId,
                locationId: query.locationId
            })
        ]);

        const nextCategories = uniqueById(
            toArray(categoriesResponse)
                .filter((item) => item?.id !== null && item?.id !== undefined)
                .map((item) => ({id: item.id, name: item.name}))
        );
        const nextLocations = uniqueById(
            toArray(locationsResponse)
                .filter((item) => item?.id !== null && item?.id !== undefined)
                .map((item) => ({id: item.id, name: item.name}))
        );
        const nextPartners = uniqueById(
            toArray(partnersResponse)
                .filter((item) => item?.id !== null && item?.id !== undefined)
                .map((item) => ({id: item.id, name: item.name}))
        );

        setCategories([{id: "all", name: "Alle categorieën"}, ...nextCategories]);
        setLocations([{id: "all", name: "Alle locaties"}, ...nextLocations]);
        setPartners([{id: "all", name: "Alle partners"}, ...nextPartners]);
    }, []);

    const fetchAccessories = useCallback(async (query) => {
        const response = await getAccessoriesAPI(query);
        const items = toArray(response);

        setAccessories(items);
        setAccessoryIndex((prev) => {
            const next = {...prev};
            items.forEach((item) => {
                if (!item?.id) return;
                next[String(item.id)] = item;
            });
            return next;
        });
    }, []);

    const loadAccessoriesData = useCallback(async () => {
        const query = buildQuery();
        setIsLoadingAccessories(true);
        setAccessoriesError("");

        try {
            await Promise.all([
                fetchFilters(query),
                fetchAccessories(query)
            ]);
        } catch (error) {
            console.error("Failed to load accessories", error);
            setAccessoriesError("Het laden van accessoires is mislukt. Probeer opnieuw.");
        } finally {
            setIsLoadingAccessories(false);
        }
    }, [buildQuery, fetchFilters, fetchAccessories]);

    const filteredLocationList = useMemo(() => {
        const query = locationSearch.trim().toLowerCase();
        if (!query) return locations;

        return locations.filter((location) => {
            if (location.id === "all") return true;
            return String(location.name).toLowerCase().includes(query);
        });
    }, [locations, locationSearch]);

    const selectedLocationLabel = useMemo(() => {
        const currentLocation = locations.find((location) => String(location.id) === String(selectedLocation));
        return currentLocation?.name || "Alle locaties";
    }, [locations, selectedLocation]);

    const buildCheckoutPayload = useCallback(() => {
        const payload = {
            accessories_purchase: [],
            accessories_rent: []
        };

        Object.entries(cart).forEach(([accessoryId, state]) => {
            const item = accessoryIndex[String(accessoryId)];
            if (!item) return;

            const normalized = normalizeCartEntry(state);
            const parsedAccessoryId = Number(accessoryId);
            if (!Number.isFinite(parsedAccessoryId)) return;

            if (hasPositivePrice(item?.prices?.purchase_including_vat) && normalized.purchaseQty > 0) {
                payload.accessories_purchase.push({
                    accessoryId: parsedAccessoryId,
                    quantity: normalized.purchaseQty
                });
            }

            if (hasPositivePrice(item?.prices?.rental_including_vat) && normalized.rentQty > 0) {
                payload.accessories_rent.push({
                    accessoryId: parsedAccessoryId,
                    quantity: normalized.rentQty,
                    days: normalized.rentDays
                });
            }
        });

        return payload;
    }, [cart, accessoryIndex]);

    const cartLines = useMemo(() => {
        return Object.entries(cart).reduce((lines, [accessoryId, state]) => {
            const item = accessoryIndex[String(accessoryId)];
            if (!item) return lines;

            const normalized = normalizeCartEntry(state);
            const purchasePrice = Number(item?.prices?.purchase_including_vat);
            const rentalPrice = Number(item?.prices?.rental_including_vat);
            const canBuy = hasPositivePrice(purchasePrice);
            const canRent = hasPositivePrice(rentalPrice);

            if (canBuy && normalized.purchaseQty > 0) {
                lines.push({
                    key: `buy-${accessoryId}`,
                    accessoryId: String(accessoryId),
                    lineType: "purchase",
                    title: `${item.name} (koop)`,
                    subtitle: `${normalized.purchaseQty} stuks`,
                    total: normalized.purchaseQty * purchasePrice,
                    depositTotal: 0
                });
            }

            if (canRent && normalized.rentQty > 0) {
                const depositPrice = Number(item?.prices?.deposit_including_vat);
                const depositTotal = hasPositivePrice(depositPrice) ? normalized.rentQty * depositPrice : 0;
                const rentalTotal = normalized.rentQty * normalized.rentDays * rentalPrice;

                lines.push({
                    key: `rent-${accessoryId}`,
                    accessoryId: String(accessoryId),
                    lineType: "rent",
                    title: `${item.name} (huur)`,
                    subtitle: `${normalized.rentQty} x ${normalized.rentDays} dag(en)`,
                    total: rentalTotal + depositTotal,
                    depositTotal
                });
            }

            return lines;
        }, []);
    }, [cart, accessoryIndex]);

    const cartTotal = useMemo(() => cartLines.reduce((sum, line) => sum + line.total, 0), [cartLines]);
    const cartItemsCount = useMemo(
        () => Object.entries(cart).reduce((sum, [accessoryId, state]) => {
            const item = accessoryIndex[String(accessoryId)];
            if (!item) return sum;

            const normalized = normalizeCartEntry(state);
            const purchaseQty = hasPositivePrice(item?.prices?.purchase_including_vat) ? normalized.purchaseQty : 0;
            const rentQty = hasPositivePrice(item?.prices?.rental_including_vat) ? normalized.rentQty : 0;

            return sum + purchaseQty + rentQty;
        }, 0),
        [cart, accessoryIndex]
    );

    const isContactValid = useMemo(() => {
        return Boolean(checkoutContact.name.trim())
            && isValidEmail(checkoutContact.email)
            && Boolean(checkoutContact.phone.trim());
    }, [checkoutContact]);

    const resetCheckoutFlow = useCallback(() => {
        setCheckoutStep("cart");
        setCheckoutError("");
        setCheckoutNotice("");
        setPreflightResult(null);
        setDraftOrderResult(null);
        setPaymentResult(null);
        setIsCheckoutLoading(false);
        draftIdempotencyKeyRef.current = "";
        paymentIdempotencyKeyRef.current = "";
    }, []);

    const runCheckoutPreflight = useCallback(async () => {
        const payload = buildCheckoutPayload();
        if (payload.accessories_purchase.length === 0 && payload.accessories_rent.length === 0) {
            setCheckoutError("Je winkelwagen is leeg.");
            return null;
        }

        const authToken = getTokenFromStorage();
        setIsCheckoutLoading(true);
        setCheckoutError("");
        setCheckoutNotice("");

        try {
            const response = await preflightAccessoriesCheckoutAPI({
                payload,
                authToken,
                catalog: accessoryIndex
            });

            setPreflightResult(response);
            const errorText = parseCheckoutErrors(response);
            if (errorText) {
                setCheckoutError(errorText);
            }
            return response;
        } catch (error) {
            setCheckoutError(error?.message || "Preflight checkout is mislukt.");
            return null;
        } finally {
            setIsCheckoutLoading(false);
        }
    }, [accessoryIndex, buildCheckoutPayload]);

    const openPaymentForOrder = useCallback(async (orderId) => {
        if (!orderId) {
            setCheckoutError("Ordernummer ontbreekt.");
            return;
        }

        const authToken = getTokenFromStorage();
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        const returnUrl = `${origin}/accessoires/result/${encodeURIComponent(String(orderId))}`;
        const cancelUrl = `${origin}/accessoires/result/${encodeURIComponent(String(orderId))}?payment_status=cancelled`;
        const webhookUrl = `${origin}/public/payments/webhook/accessories`;

        setIsCheckoutLoading(true);
        setCheckoutError("");

        try {
            if (!paymentIdempotencyKeyRef.current) {
                paymentIdempotencyKeyRef.current = createIdempotencyKey("acc-payment");
            }

            const paymentResponse = await createAccessoriesOrderPaymentAPI({
                orderId,
                returnUrl,
                cancelUrl,
                webhookUrl,
                authToken,
                idempotencyKey: paymentIdempotencyKeyRef.current
            });

            setPaymentResult(paymentResponse);
            setCheckoutStep("payment");

            if (paymentResponse?.payment_url && typeof window !== "undefined") {
                window.open(paymentResponse.payment_url, "_blank", "noopener,noreferrer");
            }

            navigate(`/accessoires/result/${encodeURIComponent(String(orderId))}?payment_status=pending`);
        } catch (error) {
            setCheckoutError(error?.message || "Betaling starten is mislukt.");
        } finally {
            setIsCheckoutLoading(false);
        }
    }, [navigate]);

    const createDraftAndProceedToPayment = useCallback(async ({contact}) => {
        const payload = buildCheckoutPayload();
        const authToken = getTokenFromStorage();

        setIsCheckoutLoading(true);
        setCheckoutError("");
        setCheckoutNotice("");

        try {
            if (!draftIdempotencyKeyRef.current) {
                draftIdempotencyKeyRef.current = createIdempotencyKey("acc-draft");
            }

            const draftResponse = await createAccessoriesDraftOrderAPI({
                payload,
                contact,
                customerId: null,
                authToken,
                idempotencyKey: draftIdempotencyKeyRef.current,
                catalog: accessoryIndex
            });

            setDraftOrderResult(draftResponse);
            const errorText = parseCheckoutErrors(draftResponse);
            if (errorText) {
                setCheckoutError(errorText);
            }

            if (draftResponse?.next_action === "collect_contact") {
                setCheckoutStep("contact");
                return;
            }

            if (draftResponse?.next_action === "register" || draftResponse?.status === "awaiting_registration") {
                setCheckoutStep("registration");
                setCheckoutNotice("Voor huurproducten moet je eerst inloggen of registreren in KAV2GO.");
                return;
            }

            if (draftResponse?.next_action === "pay" || draftResponse?.status === "awaiting_payment") {
                await openPaymentForOrder(draftResponse.order_id);
                return;
            }

            if (draftResponse?.status === "paid") {
                navigate(`/accessoires/result/${encodeURIComponent(String(draftResponse.order_id))}?payment_status=paid`);
                return;
            }

            setCheckoutError("Onbekende checkout status ontvangen.");
        } catch (error) {
            setCheckoutError(error?.message || "Bestelling aanmaken is mislukt.");
        } finally {
            setIsCheckoutLoading(false);
        }
    }, [accessoryIndex, buildCheckoutPayload, navigate, openPaymentForOrder]);

    const renderCards = useCallback((items) => items.map((item) => {
        const key = String(item.id);
        const state = normalizeCartEntry(cart[key] || DEFAULT_CART_STATE);
        const canRent = hasRentalPrice(item);
        const canBuy = hasPurchasePrice(item);

        return (
            <article className="front-accessoires-card" key={item.id} onClick={() => setSelectedProduct(item)}>
                <div className="front-accessoires-card__circle">
                    {item.image ? (
                        <img src={item.image} alt={item.name}/>
                    ) : (
                        <i className="bi bi-box-seam" aria-hidden="true"></i>
                    )}
                </div>
                <h3>{item.name}</h3>
                <p className="front-accessoires-card__partner">{resolveAccessoryPartner(item)}</p>

                <div className="front-accessoires-card__pricing">
                    {canRent ? (
                        <span>Huur: {formatPriceEur(item.prices.rental_including_vat)} / dag</span>
                    ) : null}
                    {canBuy ? (
                        <span>Koop: {formatPriceEur(item.prices.purchase_including_vat)}</span>
                    ) : null}
                </div>

                <div className="front-accessoires-card__controls" onClick={(event) => event.stopPropagation()}>
                    {canRent && (
                        <div className="front-accessoires-card__controlBlock">
                            <div className="front-accessoires-days front-accessoires-days--tight">
                                <label>Aantal</label>
                                <button
                                    type="button"
                                    className="front-accessoires-days__btn"
                                    onClick={() => updateCart(item.id, (prev) => ({...prev, rentQty: prev.rentQty - 1}))}
                                >
                                    <i className="bi bi-dash"></i>
                                </button>
                                <input
                                    type="text"
                                    value={state.rentQty}
                                    readOnly
                                    aria-label="Huren aantal"
                                    className="front-accessoires-valueInput"
                                />
                                <button
                                    type="button"
                                    className="front-accessoires-days__btn"
                                    onClick={() => updateCart(item.id, (prev) => ({...prev, rentQty: prev.rentQty + 1}))}
                                >
                                    <i className="bi bi-plus"></i>
                                </button>
                            </div>
                            <div className="front-accessoires-days">
                                <label>Dagen</label>
                                <button
                                    type="button"
                                    className="front-accessoires-days__btn"
                                    onClick={() => updateCart(item.id, (prev) => ({...prev, rentDays: prev.rentDays - 1}))}
                                >
                                    <i className="bi bi-dash"></i>
                                </button>
                                <input
                                    type="number"
                                    min="1"
                                    value={state.rentDays}
                                    className="front-accessoires-valueInput"
                                    onChange={(event) => updateCart(item.id, (prev) => ({...prev, rentDays: event.target.value}))}
                                />
                                <button
                                    type="button"
                                    className="front-accessoires-days__btn"
                                    onClick={() => updateCart(item.id, (prev) => ({...prev, rentDays: prev.rentDays + 1}))}
                                >
                                    <i className="bi bi-plus"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {canBuy && (
                        <div className="front-accessoires-card__controlBlock">
                            <div className="front-accessoires-days front-accessoires-days--tight">
                                <label>Kopen</label>
                                <button
                                    type="button"
                                    className="front-accessoires-days__btn"
                                    onClick={() => updateCart(item.id, (prev) => ({...prev, purchaseQty: prev.purchaseQty - 1}))}
                                >
                                    <i className="bi bi-dash"></i>
                                </button>
                                <input
                                    type="text"
                                    value={state.purchaseQty}
                                    readOnly
                                    aria-label="Kopen aantal"
                                    className="front-accessoires-valueInput"
                                />
                                <button
                                    type="button"
                                    className="front-accessoires-days__btn"
                                    onClick={() => updateCart(item.id, (prev) => ({...prev, purchaseQty: prev.purchaseQty + 1}))}
                                >
                                    <i className="bi bi-plus"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </article>
        );
    }), [cart, formatPriceEur, hasPurchasePrice, hasRentalPrice, resolveAccessoryPartner, updateCart]);

    useEffect(() => {
        loadAccessoriesData();
    }, [loadAccessoriesData]);

    useEffect(() => {
        if (typeof window === "undefined" || !window.localStorage) return;
        try {
            window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
        } catch (error) {
            console.warn("Accessoires cart write error", error);
        }
    }, [cart]);

    useEffect(() => {
        if (!isCartOpen) return;
        resetCheckoutFlow();
    }, [cart, isCartOpen, resetCheckoutFlow]);

    useEffect(() => {
        const onClickOutside = (event) => {
            if (!locationDropdownRef.current) return;
            if (!locationDropdownRef.current.contains(event.target)) {
                setIsLocationDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (selectedCategory === "all") return;
        if (categories.some((item) => String(item.id) === String(selectedCategory))) return;
        setSelectedCategory("all");
    }, [categories, selectedCategory]);

    useEffect(() => {
        if (selectedPartner === "all") return;
        if (partners.some((item) => String(item.id) === String(selectedPartner))) return;
        setSelectedPartner("all");
    }, [partners, selectedPartner]);

    useEffect(() => {
        if (selectedLocation === "all") return;
        if (locations.some((item) => String(item.id) === String(selectedLocation))) return;
        setSelectedLocation("all");
    }, [locations, selectedLocation]);

    useEffect(() => {
        const requestedLocationId = searchParams.get("locationId");
        const requestedLocationName = searchParams.get("location");
        const requestKey = requestedLocationId
            ? `id:${requestedLocationId}`
            : requestedLocationName
                ? `name:${requestedLocationName}`
                : "";

        if (!requestKey) return;
        if (appliedSearchLocationRef.current === requestKey) return;

        let matched = null;

        if (requestedLocationId) {
            matched = locations.find(
                (location) => location.id !== "all" && String(location.id) === String(requestedLocationId)
            );
        }

        // Backward compatibility for older links with location name.
        if (!matched && requestedLocationName) {
            const normalized = requestedLocationName.trim().toLowerCase();
            matched = locations.find(
                (location) => location.id !== "all" && String(location.name).trim().toLowerCase() === normalized
            );
        }

        if (matched) {
            setSelectedLocation(String(matched.id));
            appliedSearchLocationRef.current = requestKey;
        }
    }, [searchParams, locations]);

    const onCartIconClick = () => {
        setIsCartOpen((prev) => {
            const next = !prev;
            if (next) {
                resetCheckoutFlow();
            }
            return next;
        });
    };

    const onProceedCart = async () => {
        const preflightResponse = await runCheckoutPreflight();
        if (!preflightResponse || preflightResponse?.can_checkout !== true) return;

        if (preflightResponse?.split_required) {
            setCheckoutError(preflightResponse?.split_reason || "Deze bestelling moet opgesplitst worden.");
            return;
        }

        if (preflightResponse.checkout_type === "purchase_only") {
            setCheckoutStep("contact");
            setCheckoutNotice("Vul je contactgegevens in om je bestelling af te ronden.");
            return;
        }

        const authToken = getTokenFromStorage();
        if (preflightResponse.require_registration && !authToken) {
            setCheckoutStep("registration");
            setCheckoutNotice("Voor huurproducten moet je eerst inloggen of registreren in KAV2GO.");
            return;
        }

        await createDraftAndProceedToPayment({contact: null});
    };

    const onContactInputChange = (field, value) => {
        setCheckoutContact((prev) => ({...prev, [field]: value}));
    };

    const onSubmitContact = async (event) => {
        event.preventDefault();
        if (!isContactValid) {
            setCheckoutError("Vul naam, e-mail en telefoonnummer correct in.");
            return;
        }
        await createDraftAndProceedToPayment({contact: checkoutContact});
    };

    const onRemoveCartLine = (line) => {
        if (!line?.accessoryId || !line?.lineType) return;

        updateCart(line.accessoryId, (prev) => {
            if (line.lineType === "purchase") {
                return {...prev, purchaseQty: 0};
            }
            if (line.lineType === "rent") {
                return {...prev, rentQty: 0};
            }
            return prev;
        });
    };

    return (
        <>
            <Header/>
            <div style={{height: "70px"}}></div>

            <section className="front-accessoires-page">
                <div className="container">
                    <div className="row mb-4">
                        <div className="col-12 front-accessoires-header">
                            <h1 className="red-title-1">Accessoires huren of kopen</h1>
                            <button
                                type="button"
                                className="front-accessoires-cartIcon front-accessoires-cartIcon--top"
                                onClick={onCartIconClick}
                                aria-label="Open winkelwagen"
                            >
                                <i className="bi bi-cart3"></i>
                                <span className="front-accessoires-cartIcon__text">Winkelwagen</span>
                                {cartItemsCount > 0 && (
                                    <span className="front-accessoires-cartIcon__badge">{cartItemsCount}</span>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="front-accessoires-filter">
                                <div className="front-accessoires-filter__group">
                                    <p className="front-accessoires-filter__label">Categorie</p>
                                    <select
                                        className="front-accessoires-filter__select"
                                        value={selectedCategory}
                                        onChange={(event) => setSelectedCategory(event.target.value)}
                                    >
                                        {categories.map((category) => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="front-accessoires-filter__group">
                                    <p className="front-accessoires-filter__label">Locatie</p>
                                    <div className="front-accessoires-locationSelect" ref={locationDropdownRef}>
                                        <button
                                            type="button"
                                            className="front-accessoires-locationSelect__trigger"
                                            onClick={() => setIsLocationDropdownOpen((prev) => !prev)}
                                        >
                                            <span>{selectedLocationLabel}</span>
                                            <i className={`bi ${isLocationDropdownOpen ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                                        </button>

                                        <div className={`front-accessoires-locationSelect__menu ${isLocationDropdownOpen ? "is-open" : ""}`}>
                                            <input
                                                className="front-accessoires-locationSelect__search"
                                                type="text"
                                                placeholder="Zoek locatie"
                                                value={locationSearch}
                                                onChange={(event) => setLocationSearch(event.target.value)}
                                            />
                                            <div className="front-accessoires-locationSelect__list">
                                                {filteredLocationList.map((location) => (
                                                    <button
                                                        key={location.id}
                                                        type="button"
                                                        className={`front-accessoires-locationSelect__item ${String(selectedLocation) === String(location.id) ? "is-active" : ""}`}
                                                        onClick={() => {
                                                            setSelectedLocation(String(location.id));
                                                            setIsLocationDropdownOpen(false);
                                                        }}
                                                    >
                                                        {location.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="front-accessoires-filter__group">
                                    <p className="front-accessoires-filter__label">Partner</p>
                                    <select
                                        className="front-accessoires-filter__select"
                                        value={selectedPartner}
                                        onChange={(event) => setSelectedPartner(event.target.value)}
                                    >
                                        {partners.map((partner) => (
                                            <option key={partner.id} value={partner.id}>
                                                {partner.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {isLoadingAccessories && (
                                <div className="front-accessoires-cart">
                                    <p className="front-accessoires-cart__empty">Accessoires laden...</p>
                                </div>
                            )}

                            {!isLoadingAccessories && accessoriesError && (
                                <div className="front-accessoires-cart">
                                    <p className="front-accessoires-cart__empty">{accessoriesError}</p>
                                    <button type="button" className="mt-3" onClick={loadAccessoriesData}>
                                        Opnieuw proberen
                                    </button>
                                </div>
                            )}

                            {!isLoadingAccessories && !accessoriesError && (
                                <div className="front-accessoires-grid">
                                    {accessories.length > 0 ? renderCards(accessories) : (
                                        <div className="front-accessoires-cart">
                                            <p className="front-accessoires-cart__empty">Geen accessoires gevonden voor de gekozen filters.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {isCartOpen && (
                <div className="front-accessoires-cartDrawer">
                    <button
                        type="button"
                        className="front-accessoires-cartDrawer__backdrop"
                        onClick={() => setIsCartOpen(false)}
                        aria-label="Sluiten"
                    ></button>
                    <aside className="front-accessoires-cart front-accessoires-cartDrawer__panel">
                        <div className="front-accessoires-cartDrawer__header">
                            <h2>Winkelwagen</h2>
                            <button type="button" onClick={() => setIsCartOpen(false)} aria-label="Sluiten">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        {cartLines.length === 0 ? (
                            <p className="front-accessoires-cart__empty">
                                Nog geen producten gekozen.
                            </p>
                        ) : (
                            <>
                                {checkoutError && (
                                    <div className="front-accessoires-cart__status front-accessoires-cart__status--error">
                                        {checkoutError}
                                    </div>
                                )}
                                {checkoutNotice && (
                                    <div className="front-accessoires-cart__status front-accessoires-cart__status--note">
                                        {checkoutNotice}
                                    </div>
                                )}
                                {isCheckoutLoading && (
                                    <div className="front-accessoires-cart__status front-accessoires-cart__status--loading">
                                        <span className="kav-spinner" aria-hidden="true"></span>
                                        <span>Even geduld, we verwerken je bestelling...</span>
                                    </div>
                                )}
                                <ul>
                                    {cartLines.map((line) => (
                                        <li key={line.key}>
                                            <div>
                                                <strong>{line.title}</strong>
                                                <span>{line.subtitle}</span>
                                                {Number(line.depositTotal) > 0 && (
                                                    <span>Borg: {formatPriceEur(line.depositTotal)}</span>
                                                )}
                                            </div>
                                            <div className="front-accessoires-cart__lineActions">
                                                <strong>{formatPriceEur(line.total)}</strong>
                                                <button
                                                    type="button"
                                                    className="front-accessoires-cart__removeBtn"
                                                    onClick={() => onRemoveCartLine(line)}
                                                    aria-label={`Verwijder ${line.title}`}
                                                    title="Verwijderen"
                                                >
                                                    <i className="bi bi-trash3"></i>
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="front-accessoires-cart__total">
                                    <span>Totaal</span>
                                    <strong>{formatPriceEur(cartTotal)}</strong>
                                </div>
                                {checkoutStep === "contact" ? (
                                    <form className="front-accessoires-cart__contactForm" onSubmit={onSubmitContact}>
                                        <div className="front-accessoires-cart__contactGrid">
                                            <input
                                                type="text"
                                                placeholder="Naam"
                                                value={checkoutContact.name}
                                                onChange={(event) => onContactInputChange("name", event.target.value)}
                                            />
                                            <input
                                                type="email"
                                                placeholder="E-mailadres"
                                                value={checkoutContact.email}
                                                onChange={(event) => onContactInputChange("email", event.target.value)}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Telefoon"
                                                value={checkoutContact.phone}
                                                onChange={(event) => onContactInputChange("phone", event.target.value)}
                                            />
                                        </div>
                                        <div className="front-accessoires-cart__contactActions">
                                            <button
                                                type="button"
                                                className="front-accessoires-cart__secondaryBtn"
                                                onClick={() => {
                                                    setCheckoutStep("cart");
                                                    setCheckoutError("");
                                                    setCheckoutNotice("");
                                                }}
                                                disabled={isCheckoutLoading}
                                            >
                                                Terug
                                            </button>
                                            <button type="submit" disabled={isCheckoutLoading || !isContactValid}>
                                                {isCheckoutLoading ? "Verwerken..." : "Bestelling aanmaken"}
                                            </button>
                                        </div>
                                    </form>
                                ) : checkoutStep === "registration" ? (
                                    <div className="front-accessoires-cart__registration">
                                        <p>
                                            Voor huurproducten is een KAV2GO-account vereist.
                                            Log in en probeer daarna opnieuw.
                                        </p>
                                        <div className="front-accessoires-cart__contactActions">
                                            <button
                                                type="button"
                                                className="front-accessoires-cart__secondaryBtn"
                                                onClick={() => {
                                                    setCheckoutStep("cart");
                                                    setCheckoutError("");
                                                    setCheckoutNotice("");
                                                }}
                                            >
                                                Terug
                                            </button>
                                            <button type="button" onClick={() => navigate("/")}>
                                                Naar homepage
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={onProceedCart}
                                        disabled={isCheckoutLoading}
                                    >
                                        {isCheckoutLoading ? "Controleren..." : "Verder met bestellen"}
                                    </button>
                                )}

                                {paymentResult?.payment_url && checkoutStep === "payment" && (
                                    <button
                                        type="button"
                                        className="front-accessoires-cart__secondaryBtn"
                                        onClick={() => window.open(paymentResult.payment_url, "_blank", "noopener,noreferrer")}
                                    >
                                        Open betaalpagina opnieuw
                                    </button>
                                )}

                                {draftOrderResult?.order_id && (
                                    <p className="front-accessoires-cart__meta">
                                        Bestelnummer: {draftOrderResult.order_id}
                                    </p>
                                )}
                                {preflightResult?.checkout_type && (
                                    <p className="front-accessoires-cart__meta">
                                        Checkout type: {String(preflightResult.checkout_type).replaceAll("_", " ")}
                                    </p>
                                )}
                            </>
                        )}
                    </aside>
                </div>
            )}

            {selectedProduct && (
                <div className="front-accessoires-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="front-accessoires-modal__backdrop"
                        onClick={() => setSelectedProduct(null)}
                        aria-label="Sluiten"
                    ></button>
                    <div className="front-accessoires-modal__card">
                        <button type="button" className="front-accessoires-modal__close" onClick={() => setSelectedProduct(null)}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                        {selectedProduct.image ? (
                            <div className="front-accessoires-modal__imageWrap">
                                <img src={selectedProduct.image} alt={selectedProduct.name}/>
                            </div>
                        ) : null}
                        <h2>{selectedProduct.name}</h2>
                        <p className="front-accessoires-modal__partner">
                            Partner: {resolveAccessoryPartner(selectedProduct)}
                        </p>
                        <p className="front-accessoires-modal__description">{selectedProduct.description || "-"}</p>

                        <div className="front-accessoires-modal__prices">
                            {hasRentalPrice(selectedProduct) ? (
                                <div>
                                    <span>Huurprijs</span>
                                    <strong>{formatPriceEur(selectedProduct.prices.rental_including_vat)} / dag</strong>
                                </div>
                            ) : null}
                            {hasPurchasePrice(selectedProduct) ? (
                                <div>
                                    <span>Koopprijs</span>
                                    <strong>{formatPriceEur(selectedProduct.prices.purchase_including_vat)}</strong>
                                </div>
                            ) : null}
                        </div>

                        <div className="front-accessoires-modal__locations">
                            <h3>Beschikbare locaties</h3>
                            <ul>
                                {(Array.isArray(selectedProduct.locations) ? selectedProduct.locations : []).map((location) => {
                                    const address = location?.address || {};
                                    const addressLine = [
                                        address.street,
                                        address.house_number,
                                        address.house_number_extension,
                                        address.zip_code,
                                        address.city
                                    ].filter(Boolean).join(" ");

                                    return (
                                        <li key={`${selectedProduct.id}-${location.id}`}>
                                            {location.name}{addressLine ? ` — ${addressLine}` : ""}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <Footer/>
        </>
    );
};

export default AccessoiresPage;
